import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { GraduationCap, Check, X, Clock, Users, Loader2, Search, Eye, Mail, MessageCircle, Send, ChevronDown, ChevronUp, AlertTriangle, Activity } from 'lucide-react';
import { apiClient } from '../../api/client';
import { IssueLicenseInline, LicensesTab } from '../../components/admin/SchoolLicensePanel';
import { StudentEngagementCard } from '../../components/admin/StudentEngagementCard';

const STATUS_STYLES = {
  pending_approval: { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
  active: { bg: '#ECFDF5', color: '#059669', label: 'Active' },
  rejected: { bg: '#FEF2F2', color: '#DC2626', label: 'Rejected' },
  expired: { bg: '#F0F0F0', color: '#6B6257', label: 'Expired' },
};

const CAT_COLORS = { general: '#2563EB', technical: '#8B5CF6', billing: '#F59E0B', suggestion: '#10B981' };

const AdminSchools = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [approving, setApproving] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState('schools');
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const loadSchools = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.request('/api/school/admin/all');
      setSchools(data.schools || []);
    } catch {}
    setLoading(false);
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/school/admin/messages');
      setMessages(data.messages || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => { loadSchools(); loadMessages(); }, [loadSchools, loadMessages]);

  const approveSchool = async (schoolId) => {
    setApproving(schoolId);
    try {
      await apiClient.request(`/api/school/admin/approve/${schoolId}`, { method: 'POST' });
      await loadSchools();
    } catch (e) { alert(e.message); }
    setApproving(null);
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await apiClient.request(`/api/school/admin/messages/${messageId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message: replyText }),
      });
      setReplyText('');
      setReplyingTo(null);
      await loadMessages();
    } catch {}
    setSendingReply(false);
  };

  const markRead = async (messageId) => {
    await apiClient.request(`/api/school/admin/messages/${messageId}/read`, { method: 'PUT' }).catch(() => {});
    loadMessages();
  };

  const filtered = schools
    .filter(s => filter === 'all' || s.status === filter)
    .filter(s => !search || s.school_name?.toLowerCase().includes(search.toLowerCase()) || s.admin_email?.toLowerCase().includes(search.toLowerCase()));

  const pending = schools.filter(s => s.status === 'pending_approval').length;
  const active = schools.filter(s => s.status === 'active').length;

  return (
    <AdminLayout pageTitle="Schools">
      <CheatSheet pageId="schools" />
      <div data-testid="admin-schools-page">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6" data-testid="schools-tabs">
          <button onClick={() => setActiveTab('schools')}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={activeTab === 'schools' ? { backgroundColor: '#1A1209', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
            data-testid="tab-schools-list">
            <GraduationCap size={14} className="inline mr-1.5" /> Schools ({schools.length})
          </button>
          <button onClick={() => setActiveTab('licenses')}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={activeTab === 'licenses' ? { backgroundColor: '#1A1209', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
            data-testid="tab-school-licenses">
            <Check size={14} className="inline mr-1.5" /> Licenses
          </button>
          <button onClick={() => setActiveTab('engagement')}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={activeTab === 'engagement' ? { backgroundColor: '#1A1209', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
            data-testid="tab-school-engagement">
            <Activity size={14} className="inline mr-1.5" /> Engagement
          </button>
          <button onClick={() => setActiveTab('inbox')}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all relative"
            style={activeTab === 'inbox' ? { backgroundColor: '#1A1209', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
            data-testid="tab-school-inbox">
            <Mail size={14} className="inline mr-1.5" /> School Messages
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ backgroundColor: '#EF4444' }}>
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* ── Schools List Tab ────────────────────────────────── */}
        {activeTab === 'schools' && (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }}>
                <p className="text-xs" style={{ color: '#6B6257' }}>Total Schools</p>
                <p className="text-3xl font-bold" style={{ color: '#1A1209' }}>{schools.length}</p>
              </div>
              <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }}>
                <div className="flex items-center gap-2">
                  <Clock size={14} style={{ color: '#F59E0B' }} />
                  <p className="text-xs" style={{ color: '#6B6257' }}>Pending Approval</p>
                </div>
                <p className="text-3xl font-bold" style={{ color: '#F59E0B' }}>{pending}</p>
              </div>
              <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }}>
                <div className="flex items-center gap-2">
                  <Check size={14} style={{ color: '#2EAA5C' }} />
                  <p className="text-xs" style={{ color: '#6B6257' }}>Active</p>
                </div>
                <p className="text-3xl font-bold" style={{ color: '#2EAA5C' }}>{active}</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1" style={{ maxWidth: 320 }}>
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#6B6257' }} />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or email..." className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}
                  data-testid="school-search" />
              </div>
              {['all', 'pending_approval', 'active'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className="px-3 py-2 rounded-lg text-xs font-medium transition-all"
                  style={filter === f ? { backgroundColor: '#F5831F', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
                  data-testid={`filter-${f}`}>
                  {f === 'all' ? 'All' : f === 'pending_approval' ? 'Pending' : 'Active'}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto" style={{ color: '#F5831F' }} /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-sm" style={{ color: '#6B6257' }}>No schools found.</div>
            ) : (
              <div className="space-y-3">
                {filtered.map(school => {
                  const st = STATUS_STYLES[school.status] || STATUS_STYLES.expired;
                  const expanded = expandedId === school.school_id;
                  const pilotEnd = school.beta_end ? new Date(school.beta_end) : null;
                  const daysLeft = pilotEnd ? Math.max(0, Math.ceil((pilotEnd - new Date()) / 86400000)) : null;
                  return (
                    <div key={school.school_id} className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid={`school-row-${school.school_id}`}>
                      <div className="flex items-center justify-between px-5 py-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2563EB15' }}>
                            <GraduationCap size={20} style={{ color: '#2563EB' }} />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold truncate" style={{ color: '#1A1209' }}>{school.school_name}</h3>
                            <p className="text-xs" style={{ color: '#6B6257' }}>{school.school_district || school.state} | {school.school_type} | {school.admin_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                          {daysLeft !== null && school.status === 'active' && (
                            <span className="text-xs" style={{ color: daysLeft <= 14 ? '#DC2626' : '#6B6257' }}>{daysLeft}d left</span>
                          )}
                          {school.status === 'pending_approval' && (
                            <button onClick={() => approveSchool(school.school_id)} disabled={approving === school.school_id}
                              className="px-4 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#2EAA5C' }}
                              data-testid={`approve-school-${school.school_id}`}>
                              {approving === school.school_id ? <Loader2 size={14} className="animate-spin" /> : 'Approve'}
                            </button>
                          )}
                          <button onClick={() => setExpandedId(expanded ? null : school.school_id)}
                            className="p-2 rounded-lg hover:bg-gray-50" data-testid={`expand-school-${school.school_id}`}>
                            <Eye size={14} style={{ color: '#6B6257' }} />
                          </button>
                        </div>
                      </div>
                      {expanded && (
                        <div className="px-5 pb-4 border-t" style={{ borderColor: '#F0F0F0', backgroundColor: '#FAFAFA' }}>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-3 text-sm">
                            {[
                              { l: 'Contact', v: school.admin_name },
                              { l: 'Email', v: school.admin_email },
                              { l: 'Phone', v: school.admin_phone || '—' },
                              { l: 'State', v: school.state },
                              { l: 'Type', v: school.school_type },
                              { l: 'District', v: school.school_district || '—' },
                              { l: 'Grades', v: school.grade_levels?.join(', ') || '—' },
                              { l: 'Submitted', v: school.submitted_at ? new Date(school.submitted_at).toLocaleDateString() : '—' },
                            ].map(f => (
                              <div key={f.l}><p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B6257' }}>{f.l}</p><p className="font-medium" style={{ color: '#1A1209' }}>{f.v || '—'}</p></div>
                            ))}
                          </div>
                          {school.status === 'active' && (
                            <IssueLicenseInline school={school} onIssued={loadSchools} />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Licenses Tab ────────────────────────────────────── */}
        {activeTab === 'licenses' && <LicensesTab />}

        {activeTab === 'engagement' && <StudentEngagementCard />}

        {/* ── Message Inbox Tab ───────────────────────────────── */}
        {activeTab === 'inbox' && (
          <div data-testid="school-inbox">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold" style={{ color: '#1A1209' }}>School Messages</h2>
                <p className="text-xs" style={{ color: '#6B6257' }}>Messages from school administrators and principals</p>
              </div>
              {unreadCount > 0 && (
                <span className="px-3 py-1.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#EF4444' }}>
                  {unreadCount} unread
                </span>
              )}
            </div>

            {messages.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl" style={{ border: '1px solid #E0E0E0' }}>
                <Mail size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: '#6B6257' }}>No school messages yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map(m => {
                  const catColor = CAT_COLORS[m.category] || '#6B6257';
                  const isUnread = m.status === 'unread';
                  return (
                    <div key={m.message_id}
                      className="bg-white rounded-xl overflow-hidden transition-shadow"
                      style={{ border: isUnread ? '2px solid #F5831F40' : '1px solid #E0E0E0', backgroundColor: isUnread ? '#FFFCF8' : 'white' }}
                      data-testid={`inbox-msg-${m.message_id}`}>

                      {/* Header */}
                      <div className="px-5 py-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2563EB15' }}>
                              <GraduationCap size={16} style={{ color: '#2563EB' }} />
                            </div>
                            <div>
                              <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>{m.school_name}</h4>
                              <p className="text-[10px]" style={{ color: '#6B6257' }}>{m.sender_name} — {m.sender_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: `${catColor}15`, color: catColor }}>
                              {m.category?.toUpperCase()}
                            </span>
                            {m.category === 'technical' && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: '#8B5CF615', color: '#8B5CF6' }}>
                                DEV TEAM
                              </span>
                            )}
                            {isUnread && <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#F5831F' }} />}
                          </div>
                        </div>

                        <h3 className="text-sm font-bold mb-1" style={{ color: '#1A1209' }}>{m.subject}</h3>
                        <p className="text-xs leading-relaxed" style={{ color: '#555' }}>{m.message}</p>
                        <p className="text-[10px] mt-2" style={{ color: '#CCC' }}>
                          {m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>

                        {/* Replies */}
                        {m.replies?.map((r, ri) => (
                          <div key={ri} className="mt-3 ml-4 pl-4 py-2 border-l-2 rounded-r-lg" style={{ borderColor: '#F5831F', backgroundColor: '#FFF7ED' }}>
                            <p className="text-[10px] font-bold" style={{ color: '#F5831F' }}>HiBFF Reply — {r.from}</p>
                            <p className="text-xs mt-1" style={{ color: '#333' }}>{r.message}</p>
                            <p className="text-[10px] mt-1" style={{ color: '#CCC' }}>
                              {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </div>
                        ))}

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 mt-3">
                          {isUnread && (
                            <button onClick={() => markRead(m.message_id)} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold" style={{ backgroundColor: '#F0F0F0', color: '#666' }}
                              data-testid={`mark-read-${m.message_id}`}>
                              <Check size={10} className="inline mr-1" /> Mark Read
                            </button>
                          )}
                          <button onClick={() => { setReplyingTo(replyingTo === m.message_id ? null : m.message_id); setReplyText(''); }}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-white" style={{ backgroundColor: '#F5831F' }}
                            data-testid={`reply-btn-${m.message_id}`}>
                            <MessageCircle size={10} className="inline mr-1" /> Reply
                          </button>
                        </div>

                        {/* Reply form */}
                        {replyingTo === m.message_id && (
                          <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: '#F8F7F4', border: '1px solid #E8E8E8' }}>
                            <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                              placeholder="Type your reply..." rows={3}
                              className="w-full px-3 py-2 border rounded-lg text-sm resize-none mb-2" style={{ borderColor: '#E0E0E0' }}
                              data-testid={`reply-text-${m.message_id}`} />
                            <div className="flex gap-2">
                              <button onClick={() => setReplyingTo(null)} className="px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
                              <button onClick={() => handleReply(m.message_id)} disabled={!replyText.trim() || sendingReply}
                                className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 flex items-center gap-1" style={{ backgroundColor: '#F5831F' }}
                                data-testid={`send-reply-${m.message_id}`}>
                                {sendingReply ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Send Reply
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSchools;
